import { useState, useEffect, useCallback } from 'react';
import { useBorneStore } from '@/store/borne-store';
import { useCartStore } from '@/store/cart-store';
import { useConfigStore } from '@/store/config-store';
import { productCache } from '@/services/product-cache';
import { useInactivityTimer } from '@/hooks/useInactivityTimer';
import { ProductCard } from '@/components/ProductCard';
import { CartSidebar } from '@/components/CartSidebar';
import { PhoneDialog } from '@/components/PhoneDialog';
import type { Product } from '@/services/agent-client';

export function ProductsScreen() {
  const navigate = useBorneStore((s) => s.navigate);
  const screensaverTimeout = useConfigStore((s) => s.screensaverTimeout);
  const addItem = useCartStore((s) => s.addItem);
  const setPhoneNumber = useCartStore((s) => s.setPhoneNumber);
  const cartItems = useCartStore((s) => s.items);

  const showToast = useBorneStore((s) => s.showToast);
  const ticketsPrinted = useConfigStore((s) => s.ticketsPrinted);
  const maxTickets = useConfigStore((s) => s.maxTickets);

  const [products, setProducts] = useState<
    Array<{ key: string; name: string; price: number; image_url?: string }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);

  // Inactivity: return to screensaver after timeout.
  useInactivityTimer(screensaverTimeout || 30, useCallback(() => {
    navigate('screensaver');
  }, [navigate]));

  // Load products on mount.
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await productCache.getProducts();
        if (cancelled) return;

        const filtered = Object.entries(data)
          .filter(([, p]) => p.is_available_borne && p.has_sequence)
          .map(([key, p]) => ({
            key,
            name: p.name,
            price: p.price,
            image_url: p.image_url,
          }));

        setProducts(filtered);
      } catch {
        // Product cache will show stale data or empty.
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Pay flow ----

  function handlePayRequest() {
    if (useConfigStore.getState().isTicketLimitReached()) {
      showToast('Limite de tickets atteinte', 'error');
      return;
    }
    setShowPhoneDialog(true);
  }

  function handlePhoneSubmit(phone: string) {
    setPhoneNumber(phone);
    setShowPhoneDialog(false);
    navigate('payment');
  }

  function handlePhoneSkip() {
    setPhoneNumber(null);
    setShowPhoneDialog(false);
    navigate('payment');
  }

  return (
    <div className="h-full w-full flex flex-row">
      {/* ---- Left: Product grid ---- */}
      <div className="w-[70%] flex flex-col bg-surface">
        {/* Header */}
        <div className="flex items-center gap-4 p-4 border-b border-gray-800">
          <button
            type="button"
            onClick={() => navigate('screensaver')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 12H5M5 12L12 19M5 12L12 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-white">Nos Produits</h1>
          {!loading && (
            <span className="text-sm text-gray-400">
              ({products.length} produits disponibles)
            </span>
          )}
          <span className="ml-auto text-sm text-gray-400">
            Tickets: {ticketsPrinted}/{maxTickets}
          </span>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          ) : products.length === 0 ? (
            <p className="text-gray-500 text-center mt-16 text-lg">
              Aucun produit disponible
            </p>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
              {products.map((product) => (
                <ProductCard
                  key={product.key}
                  product={product}
                  quantity={cartItems[product.key]?.quantity ?? 0}
                  onTap={() => addItem(product)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---- Right: Cart sidebar ---- */}
      <div className="w-[30%]">
        <CartSidebar onPay={handlePayRequest} />
      </div>

      {/* ---- Phone dialog ---- */}
      {showPhoneDialog && (
        <PhoneDialog onSubmit={handlePhoneSubmit} onSkip={handlePhoneSkip} />
      )}
    </div>
  );
}
